import React, { useState, useRef, useEffect } from "react";

/**
 * Color swatch picker — grid of 12 preset colors + optional hex input.
 * Replaces the native <input type="color"> for a more branded UX.
 */

const PALETTES = {
  bullish: [
    "#00e676", "#26a69a", "#10b981", "#22c55e", "#a3e635", "#4ade80",
    "#22d3ee", "#06b6d4", "#0ea5e9", "#3b82f6", "#8b5cf6", "#ffffff",
  ],
  bearish: [
    "#ff3b30", "#ef5350", "#f43f5e", "#ec4899", "#e11d48", "#dc2626",
    "#f97316", "#ea580c", "#f59e0b", "#eab308", "#a855f7", "#64748b",
  ],
  background: [
    "#0b1420", "#050a13", "#0a0118", "#0f172a", "#111827", "#000000",
    "#1e1b4b", "#0c0a09", "#1c1917", "#18181b", "#f7f8fa", "#ffffff",
  ],
  grid: [
    "#1e293b", "#0f172a", "#1e1b4b", "#334155", "#27272a", "#3f3f46",
    "#374151", "#4b5563", "#525252", "#71717a", "#e2e8f0", "#f1f5f9",
  ],
};

export default function ColorSwatchPicker({ label, value, onChange, palette = "bullish", testid }) {
  const colors = PALETTES[palette] || PALETTES.bullish;
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(value || "");
  const ref = useRef(null);

  useEffect(() => setHex(value || ""), [value]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const commitHex = (v) => {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(v.trim());
    if (m) onChange(`#${m[1]}`);
  };

  return (
    <div className="flex items-center justify-between gap-2" ref={ref}>
      <span className="text-[11px] text-white/70">{label}</span>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 px-1.5 py-1 rounded-md border border-white/15 hover:border-white/40 bg-white/5"
          data-testid={testid}
          title={value}
        >
          <span className="inline-block w-4 h-4 rounded-sm border border-white/20" style={{ backgroundColor: value }} />
          <span className="text-[10px] font-mono text-white/70">{value?.slice(0, 7) || "—"}</span>
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 z-[60] p-2 rounded-lg border border-white/15 bg-[#0d1420] shadow-xl w-52" data-testid={`${testid}-menu`}>
            <div className="grid grid-cols-6 gap-1.5 mb-2">
              {colors.map((c) => (
                <button
                  key={c}
                  onClick={() => { onChange(c); setOpen(false); }}
                  className={`w-6 h-6 rounded-md border transition-transform hover:scale-110 ${value === c ? "border-accent-blue ring-2 ring-accent-blue/40" : "border-white/15"}`}
                  style={{ backgroundColor: c }}
                  title={c}
                  data-testid={`${testid}-swatch-${c.replace("#", "").toLowerCase()}`}
                />
              ))}
            </div>
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                onBlur={(e) => commitHex(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { commitHex(e.target.value); setOpen(false); } }}
                placeholder="#hexadecimal"
                className="flex-1 field-sm text-[10px] px-2 py-1 font-mono"
                data-testid={`${testid}-hex-input`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
